<script lang="ts" setup>
import { computed, ref, watch, onUnmounted } from 'vue'
import { Tag } from '@/ui/common/tag'
import exportImage from '@/assets/icons/history/export-image.svg'
import renameIcon from '@/assets/icons/threads/rename.svg'
import trash from '@/assets/icons/history/trash.svg'
import TextView from '@/ui/common/text-view/TextView.vue'
import TruncatedTextWithTooltip from '@/ui/common/truncated-text-with-tooltip/TruncatedTextWithTooltip.vue'
import ContextMenu from '@/ui/common/context-menu/ContextMenu.vue'
import { Props } from '@/ui/common/history/card/types'
import dayjs from 'dayjs'
import { Routes } from '@/config/router/routes'
import { DATE_FORMAT } from '@/consts/date'
import { useI18n } from 'vue-i18n'
import { nextTick } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps<Props>()
const router = useRouter()
const { t } = useI18n()

enum MenuItemKeys {
  RENAME = 'rename',
  EXPORT = 'export',
  DELETE = 'delete',
}

const editInputRef = ref<HTMLInputElement>()
const editWrapperRef = ref<HTMLElement>()

const menuItems = computed(() => [
  {
    key: MenuItemKeys.RENAME,
    label: t('menu.rename'),
    icon: renameIcon,
    onClick: () => props.whenClickRename && props.whenClickRename(),
    color: 'var(--gray_80)',
    isDisabled: props.isDisabled,
  },
  {
    key: MenuItemKeys.EXPORT,
    label: t('menu.export'),
    icon: exportImage,
    onClick: () => props.whenClickExport && props.whenClickExport(),
    color: 'var(--gray_80)',
    isLoading: !!props.isExporting,
    isDisabled: props.isDisabled,
  },
  {
    key: MenuItemKeys.DELETE,
    label: t('menu.delete'),
    icon: trash,
    onClick: () => props.whenClickDelete && props.whenClickDelete(),
    color: 'var(--error_50)',
    isLoading: !!props.isDeleting,
    isDisabled: props.isDisabled,
  },
])

const handleDocumentClick = (event: MouseEvent) => {
  if (!props.isRenaming || !editWrapperRef.value) return

  const target = event.target as Node
  const clickedInside = editWrapperRef.value.contains(target)

  if (!clickedInside) {
    props.whenEditSave?.()
  }
}

watch(
  () => props.isRenaming,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        editInputRef.value?.focus()
        editInputRef.value?.select()
        setTimeout(() => {
          document.addEventListener('click', handleDocumentClick)
        }, 10)
      })
    } else {
      document.removeEventListener('click', handleDocumentClick)
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
})

const handleInputKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    props.whenEditSave?.()
  } else if (e.key === 'Escape') {
    props.whenEditCancel?.()
  }
}

const handleInputBlur = () => {
  props.whenEditSave?.()
}

const handleInput = (e: Event) => {
  if (props.whenEditInput) {
    props.whenEditInput((e.target as HTMLInputElement).value)
  }
}

const handleWrapperClick = (e: MouseEvent) => {
  e.stopPropagation()
  e.preventDefault()
}

const handleLinkClick = (e: MouseEvent) => {
  if (props.isRenaming) {
    e.preventDefault()
    e.stopPropagation()
    return
  }
  router.push({ name: Routes.ChatDetail, params: { id: props.thread_id } })
}
</script>

<template>
  <div
    class="HistoryCard"
    :class="{ HistoryCard_renaming: props.isRenaming }"
    @click.prevent="handleLinkClick"
  >
    <div>
      <Tag
        :variant="props.tagType"
        :text-color="props.textColor"
        :background-color="props.backgroundColor"
        class="HistoryCard__tag"
      >
        {{ props.tagLabel }}
      </Tag>
    </div>

    <div class="HistoryCard__textContent">
      <div
        v-if="props.isRenaming"
        ref="editWrapperRef"
        class="HistoryCard__editWrapper"
        @click="handleWrapperClick"
        @click.stop
      >
        <input
          ref="editInputRef"
          :value="props.editValue"
          @input="handleInput"
          class="HistoryCard__editInput"
          @keydown="handleInputKeydown"
          @blur="handleInputBlur"
        />
      </div>

      <TextView v-else variant="roboto_14_semibold" class="HistoryCard__text">
        {{ props.text }}
      </TextView>
    </div>

    <TruncatedTextWithTooltip
      :text="props.textAdditional"
      :lines="3"
      variant="roboto_13_regular"
      color="gray_100"
      class="HistoryCard__text HistoryCard__textAdditional"
    />

    <div class="HistoryCard__footer">
      <TextView variant="roboto_13_regular" color="gray_60">
        {{ dayjs(props.date).format(DATE_FORMAT) }}
      </TextView>

      <ContextMenu :menu-items="menuItems" :is-disabled="props.isDisabled" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/media' as media;

.HistoryCard {
  display: flex;
  flex-direction: column;
  padding: 13px 8px 4px 16px;
  border: 1px solid var(--gray_20);
  border-radius: 12px;
  background-color: var(--gray_white);
  opacity: v-bind('props.isDisabled ? 0.6 : 1');
  pointer-events: v-bind('props.isDisabled ? "none" : "auto"');
  text-decoration: none;
  cursor: pointer;
  position: relative;
  max-height: 160px;
  // max-width: 300px;
  // @include media.max(media.$tablet) {
  //  max-width: 360px;
  // }
}

.HistoryCard_renaming {
  cursor: default;
}

.HistoryCard__textContent {
  margin-bottom: 13px;
  min-height: 24px;
}

.HistoryCard__editWrapper {
  position: relative;
  width: 100%;
}

.HistoryCard__editInput {
  width: 100%;
  border: 1px solid var(--brand_50);
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 13px;
  font-family: 'Roboto', sans-serif;
  color: var(--gray_80);
  background: var(--white);
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: var(--brand_70);
    box-shadow: 0 0 0 2px rgba(0, 90, 255, 0.1);
  }
}

.HistoryCard__editInput:focus {
  border-color: var(--brand_70);
  box-shadow: 0 0 0 2px rgba(0, 90, 255, 0.1);
}

.HistoryCard__text {
  padding-right: 8px;
}

.HistoryCard__textAdditional {
  margin-bottom: 13px;
  padding-right: 8px;
  flex-grow: 1;
}

.HistoryCard__tag {
  margin-bottom: 13px;
}

.HistoryCard__footer {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--gray_10);
  padding-top: 12px;
}
</style>
